/**
 * Phase 0 — centralised axios client with `withCredentials: true` so the
 * HttpOnly `mojo_session` cookie is attached to every API call.
 *
 * Strategy
 * ========
 * We install a GLOBAL default on the shared axios import:
 *
 *     axios.defaults.withCredentials = true;
 *
 * This means every existing call to `axios.get(...)`, `axios.post(...)`,
 * etc. in the codebase automatically sends the session cookie. The 14
 * files that currently read `localStorage.getItem('mojo_token')` and pass
 * `Authorization: Bearer <token>` continue to work — the backend accepts
 * both during the dual-mode cutover window.
 *
 * The named export `authClient` below is for new code that wants explicit
 * intent ("this call is authenticated"); existing code keeps using `axios`.
 *
 * Migration off localStorage
 * ==========================
 * On the LandingPage / AdminLogin / AgentLogin pages we no longer write
 * the token to localStorage — the cookie alone is the source of truth.
 * Per-call Authorization headers from existing code become redundant but
 * are harmless (backend ignores them when the cookie is present).
 */
import axios from 'axios';

// Global: every axios call sends cookies. Same-origin so no CORS work needed.
axios.defaults.withCredentials = true;

// ── Phase 0 idempotency header auto-attach ─────────────────────────────
// The backend middleware refuses any POST to a money-moving endpoint
// without an `Idempotency-Key` header. Wiring it once at the global
// axios level means every existing axios.post(...) call across the
// customer / merchant / admin / agent pages picks it up automatically.
// Keep this URL list aligned with middleware/idempotency.GUARDED_PREFIXES
// on the backend (lib/offline-axios.GUARDED_PREFIXES uses the same
// authoritative list).
// MUST match backend middleware/idempotency.GUARDED_PREFIXES exactly — the
// server 400s a guarded POST with no key, so a path here that the server does
// not guard is harmless, but a path the SERVER guards that is missing here
// breaks that call. Realigned to real routes 2026-08-14.
const _IDEMPOTENT_PREFIXES = [
  '/api/wallet/topup',
  '/api/wallet/transfer',
  '/api/limes/subscriptions',
  '/api/limes/order',
  '/api/limes/setup/store-account-customer',
  '/api/postbank/withdraw',
  '/api/postbank/deposit',
  '/api/ace-token',
  '/api/insurance/pay',
  '/api/insurance/collect-premium',
  '/api/insurance/create-policy',
  '/api/payment/card',
  '/api/card/payout',
  '/api/money-transfer',
  '/api/grants/payout',
];

function _newIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function _isGuarded(url) {
  if (!url) return false;
  return _IDEMPOTENT_PREFIXES.some((p) => url.includes(p));
}

axios.interceptors.request.use((config) => {
  const method = String(config.method || 'get').toUpperCase();
  if (method !== 'POST') return config;
  if (!_isGuarded(config.url || '')) return config;
  // Don't clobber a caller-supplied key — the offline-axios queue and
  // the agent grant-payout page both set their own UUID per submit click
  // so a manual retry uses the same key (server dedups via cache).
  const headers = config.headers || {};
  const has = headers['Idempotency-Key']
           || headers['idempotency-key']
           || (headers.common && headers.common['Idempotency-Key']);
  if (has) return config;
  config.headers = { ...headers, 'Idempotency-Key': _newIdempotencyKey() };
  return config;
});


// ── Auto-logout on expired session ─────────────────────────────────────
// Frank's old test session expired (cookie max-age 24h) and the next API
// call returned 401 "Not authenticated". The UI bubbled a raw error
// instead of putting him back on the login screen. Fix is a single
// global response interceptor: on 401 with no useful detail (i.e. NOT
// step_up_required, NOT recovery_cooldown — those are handled
// elsewhere), clear local profile state and bounce to the landing page.
//
// Path excludes:
//   * Login / register endpoints — a 401 there is "wrong password",
//     don't redirect.
//   * /auth/me — used as a boot probe; the consumer handles its own
//     unauthenticated case.
//   * /webauthn/auth/begin and /webauthn/recovery/* — these intentionally
//     return non-2xx for various flows that aren't "session expired".
const _AUTH_PROBE_PATHS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/me',
  '/api/admin/login',
  '/api/agent/login',
  '/api/webauthn/auth/begin',
  '/api/webauthn/auth/finish',
  '/api/webauthn/recovery/begin',
  '/api/webauthn/recovery/verify',
];

function _isAuthProbe(url) {
  if (!url) return false;
  return _AUTH_PROBE_PATHS.some((p) => url.includes(p));
}

axios.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const url    = error?.config?.url || '';
    // Step-up retries are handled in the next interceptor; recovery
    // cooldown is a 403; user-visible per-form 401s (wrong password
    // etc.) are excluded by _isAuthProbe. What's left: a 401 from any
    // post-login route → the session has actually expired.
    const detailCode = typeof detail === 'object' ? detail?.code : null;
    const isStepUp = detailCode === 'step_up_required';
    if (
      status === 401 &&
      !isStepUp &&
      !_isAuthProbe(url) &&
      typeof window !== 'undefined'
    ) {
      try {
        // Clear the per-role profile blobs that the dashboards key off so
        // a reload doesn't think we're still logged in. The HttpOnly
        // cookie is already gone from the browser by the time we got 401;
        // nothing to clear there.
        localStorage.removeItem('mojo_user');
        localStorage.removeItem('mojo_wallet_id');
        localStorage.removeItem('mojo_admin_user');
        localStorage.removeItem('mojo_agent_user');
        // Stash a small flag so the landing page can show a "your session
        // expired — please log in again" toast on the next render.
        sessionStorage.setItem('mojo_session_expired', '1');
      } catch (_) { /* sandboxed iframe */ }
      // Only navigate if we're not already on a public surface (the
      // landing page or any /admin/login / /agent/login screen).
      const path = window.location?.pathname || '';
      const onPublicPage = path === '/' || path === '/recovery'
        || path.endsWith('/login');
      if (!onPublicPage) {
        // Choose the right login page for the surface the user was on so
        // they don't bounce from the admin app into the customer landing.
        let dest = '/';
        if (path.startsWith('/admin')) dest = '/admin/login';
        else if (path.startsWith('/agent')) dest = '/agent/login';
        window.location.href = dest;
      }
    }
    return Promise.reject(error);
  }
);


// ── Phase 3 step-up auto-ceremony ──────────────────────────────────────
// When the backend returns 401 with detail.code='step_up_required', we
// run the WebAuthn / OTP ceremony, attach the resulting ticket to a
// retry of the original request, and bubble the eventual success
// response up to the caller as if step-up never happened.
//
// This means every legacy axios.post(...) on the customer + merchant
// dashboards gets step-up handling for free — no per-page changes.
// Pages that DO want to run the ceremony themselves (Agent grant
// payout) continue to work because they attach the ticket BEFORE the
// request is sent; this interceptor only fires when the server says
// step-up is needed AND no ticket is present.
axios.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const cfg     = error?.config || {};
    const status  = error?.response?.status;
    const detail  = error?.response?.data?.detail;
    const needsStepUp =
      status === 401 &&
      typeof detail === 'object' &&
      detail?.code === 'step_up_required';

    // Avoid infinite loops: a single retry per original request. If the
    // retried call also returns step_up_required something is badly
    // wrong (e.g. clock skew, server bug); let the error propagate.
    if (!needsStepUp || cfg._stepupRetried) {
      return Promise.reject(error);
    }

    // Lazy-import requireStepUp to dodge the module-load order trap
    // (stepup-client imports webauthn-client which imports axios — pulling
    // it eagerly here would force the chain to resolve at auth-client
    // load time).
    let requireStepUp;
    try {
      const mod = await import('./stepup-client');
      requireStepUp = mod.requireStepUp;
    } catch (e) {
      return Promise.reject(error);
    }

    // Rebuild the "what was the user trying to do" metadata from the
    // original request body so the ticket binds to the same canonical
    // summary the backend will recompute.
    let body = {};
    try {
      body = typeof cfg.data === 'string' ? JSON.parse(cfg.data) : (cfg.data || {});
    } catch (_) { /* body wasn't JSON — leave summary empty */ }

    const summary = {};
    for (const k of ['recipient_wallet_id', 'recipient', 'merchant_id',
                     'description', 'reference', 'recipient_phone']) {
      if (body && body[k] != null) summary[k] = String(body[k]);
    }

    const action = detail.action || 'unknown';
    const amountZar = Number(detail.amount_zar
                             || body.amount_zar
                             || body.amount
                             || 0);

    let ticket;
    try {
      ticket = await requireStepUp({
        action,
        amountZar,
        summary,
        promptOtp: async (phoneHint) => {
          // Minimal built-in OTP modal: browser prompt. Pages that want
          // a proper UI override by attaching their own ticket BEFORE
          // calling axios.post (so this interceptor doesn't fire).
          if (typeof window === 'undefined' || !window.prompt) return null;
          const code = window.prompt(
            `An SMS code has been sent to ${phoneHint}. Enter it to confirm this transaction.`
          );
          return code ? String(code).trim() : null;
        },
      });
    } catch (e) {
      // User cancelled the biometric prompt, no method available, etc.
      return Promise.reject(error);
    }

    if (!ticket || !ticket.ticketId) {
      // Skipped (e.g. step-up actually not required by the time we got
      // here, but the server said otherwise — propagate the original
      // error rather than silently retrying with no ticket).
      return Promise.reject(error);
    }

    cfg._stepupRetried = true;
    cfg.headers = {
      ...(cfg.headers || {}),
      'X-Step-Up-Ticket': ticket.ticketId,
    };
    return axios.request(cfg);
  }
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const authClient = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// 401 interceptor — kick the user to the landing page if their cookie
// has expired. Skip on /auth/me probes (used at boot to check state).
authClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error?.response?.status === 401 &&
      !error?.config?.url?.includes('/auth/me')
    ) {
      // Clear any legacy localStorage state, then redirect.
      try {
        localStorage.removeItem('mojo_token');
        localStorage.removeItem('mojo_admin_token');
      } catch (_) { /* ignore */ }
      if (typeof window !== 'undefined' && window.location?.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export { axios, API, BACKEND_URL };
